/**
 * @fileoverview Story viewer page — renders a paginated story with prev/next navigation.
 */

import { useState, useEffect } from 'preact/hooks';
import { BasePage } from './BasePage.js';
import { StoryService, type Story } from '../../services/StoryService.js';
import { tapProps } from '../../utilities/tapProps.js';
import { storyStrings } from '../../i18n/stories.js';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function StoryViewer({ storyId }: { storyId: string }) {
    const [story, setStory] = useState<Story | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const navigate = (path: string) => (window as any).__appNavigate?.(path);

    useEffect(() => {
        setStory(null);
        StoryService.loadStory(storyId).then(s => {
            if (!s) {
                console.warn('Story not found:', storyId);
                navigate('/stories');
                return;
            }
            if (!s.isFree && !s.isPurchased) {
                alert(storyStrings.purchaseFirst);
                navigate('/stories');
                return;
            }
            const progress = StoryService.getStoryProgress(storyId);
            setCurrentPage(progress ? progress.currentPage : 0);
            setStory(s);
            StoryService.preloadImages(s);
        });
    }, [storyId]);

    if (!story) {
        return <div class="story-loading">{storyStrings.loading}</div>;
    }

    const page = story.pages[currentPage];
    const isLast = currentPage === story.pageCount - 1;

    const next = () => {
        if (!isLast) {
            const n = currentPage + 1;
            setCurrentPage(n);
            StoryService.saveProgress(storyId, n);
        } else {
            StoryService.markCompleted(storyId);
            StoryService.saveProgress(storyId, 0);
            alert(storyStrings.storyCompleted);
            navigate('/stories');
        }
    };

    const prev = () => {
        if (currentPage > 0) {
            const n = currentPage - 1;
            setCurrentPage(n);
            StoryService.saveProgress(storyId, n);
        }
    };

    return (
        <div class="story-viewer">
            <div class="story-header">
                <button class="story-back-btn" {...tapProps(() => navigate('/stories'))}>{storyStrings.back}</button>
                <h3 class="story-title">{story.title}</h3>
            </div>

            <div class="story-content" id="storyContent">
                <img class="story-image" src={page.imageUrl} alt={page.altText} />
                <div class="story-text-overlay">
                    <p>{page.text}</p>
                </div>
            </div>

            <div class="story-navigation">
                <button class="story-nav-btn prev" {...tapProps(prev)} disabled={currentPage === 0}>
                    {storyStrings.previous}
                </button>
                <span class="story-progress">{storyStrings.pageOf(currentPage + 1, story.pageCount)}</span>
                <button class="story-nav-btn next" {...tapProps(next)}>
                    {isLast ? storyStrings.finish : storyStrings.next}
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page class
// ---------------------------------------------------------------------------

export class StoryViewerPage extends BasePage {
    constructor() {
        super('storyViewerPage');
    }

    protected override async render(params?: Record<string, string> | null): Promise<void> {
        const storyId = params?.id;
        if (!storyId) { this.navigate('/stories'); return; }
        this.renderPreact(<StoryViewer storyId={storyId} />);
    }
}
